import React, { useState, useEffect } from "react";
import { useUser } from "../contexts/UserContext";
import { Button } from "./ui/Button";
import { Alert } from "./ui/Alert";

export default function Login({ onLogin, logoutMsg }) {
  const { setUser } = useUser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showLogoutMsg, setShowLogoutMsg] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (logoutMsg) {
      setShowLogoutMsg(true);
      const timer = setTimeout(() => {
        setShowLogoutMsg(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [logoutMsg]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (email && password) {
      setIsLoading(true);
      
      try {
        const response = await fetch("/api/auth/login/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email,
            password: password,
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setUser(
            { ...data.user, permissions: data.permissions || {} },
            { access: data.access, refresh: data.refresh },
          );
          setTimeout(() => {
            setIsLoading(false);
            onLogin();
          }, 1500);
        } else {
          setIsLoading(false);
          setLoginError(data.error || "ログインに失敗しました");
        }
      } catch (error) {
        setIsLoading(false);
        setLoginError("ネットワークエラーが発生しました");
        console.error("Login error:", error);
      }
    } else {
      setLoginError("メールアドレスとパスワードを入力してください");
    }
  };

  const inputClass =
    "w-full pl-10 pr-4 py-3 border border-prodia-border rounded-xl focus:border-prodia-primary focus:ring-2 focus:ring-prodia-primary/30 outline-none transition-all text-prodia-text-primary placeholder:text-prodia-text-muted bg-prodia-background focus:bg-prodia-surface text-sm";

  return (
    <div className="min-h-screen bg-prodia-background flex items-center justify-center px-4 py-12">

      {showLogoutMsg && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-[9999] max-w-sm w-full animate-slideDown">
          <Alert variant="success" title={logoutMsg || "ログアウトしました"} />
        </div>
      )}

      <div className="w-full max-w-md animate-fadeInUp">

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-prodia-primary tracking-tight mb-2">Prodia</h1>
          <p className="text-prodia-text-secondary text-sm">営業チームの力を最大化するビジネスプラットフォーム</p>
        </div>

        <div className="bg-prodia-surface rounded-xl shadow-prodia-md border border-prodia-border p-8">

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-prodia-text-primary">ログイン</h2>
            <p className="text-sm text-prodia-text-secondary mt-0.5">アカウント情報を入力してください</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-prodia-text-secondary">メールアドレス</label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                  <i className="fas fa-envelope text-prodia-text-muted text-sm"></i>
                </div>
                <input
                  type="email"
                  placeholder="yamada@prodia.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-prodia-text-secondary">パスワード</label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                  <i className="fas fa-lock text-prodia-text-muted text-sm"></i>
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="パスワードを入力"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-prodia-text-muted hover:text-prodia-text-secondary transition-colors"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                </button>
              </div>
            </div>

            {loginError && (
              <Alert variant="danger">{loginError}</Alert>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              size="lg"
              className="w-full mt-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>認証中...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt"></i>
                  <span>ログイン</span>
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-prodia-text-muted mt-6">
          © 2026 Prodia. All rights reserved.
        </p>
      </div>
    </div>
  );
}

const existingStyle = document.head.querySelector('[data-login-page-animations]');

if (!existingStyle) {
  const style = document.createElement('style');
  style.setAttribute('data-login-page-animations', 'true');
  style.textContent = `
    @keyframes slideDown {
      0% { opacity: 0; transform: translateX(-50%) translateY(-30px); }
      100% { opacity: 1; transform: translateX(-50%) translateY(0px); }
    }
    @keyframes fadeInUp {
      0% { opacity: 0; transform: translateY(30px); }
      100% { opacity: 1; transform: translateY(0px); }
    }
    .animate-slideDown { animation: slideDown 0.5s ease-out forwards; }
    .animate-fadeInUp { animation: fadeInUp 0.6s ease-out forwards; opacity: 0; }
  `;
  document.head.appendChild(style);
}
